/**
 * The pure half of `useMetricsHistoryDoor` — same split as `appConfigFlagLogic`
 * behind `useIapEnabled`/`useFunnelEnabled`, and for the same reason: the suite
 * runs `environment: "node"` with no DOM, so a hook cannot be rendered. Move the
 * judgement out, leave the plumbing in.
 *
 * WHY THIS ONE FAILS **OPEN**, WHEN EVERY OTHER FLAG IN THIS REPO FAILS CLOSED
 * ---------------------------------------------------------------------------
 * `useIapEnabled` and `useFunnelEnabled` gate money and a signup path: a flag
 * read that goes wrong there must land on "off", because the cost of a wrong
 * "on" is a charge or a broken funnel.
 *
 * This flag gated a FAKE DOOR — a row that opened a sheet saying the feature was
 * not built yet. Since 2026-09-09 the same row opens the REAL history chooser,
 * and the fail-open default got stronger rather than weaker:
 *
 *   wrong "on"  → one extra navigation row on the home screen.
 *   wrong "off" → the ONLY signposted route into the history feature disappears.
 *                 Home's tiles open on היום, so with this row gone היסטוריה is
 *                 reachable only by noticing a tab inside a drawer.
 *
 * Which is also why the flag was NOT flipped to `false` when the fake door
 * stopped being fake: the row it controls is no longer a lie, so there is
 * nothing to switch off. It stays as a remote kill switch, nothing more.
 *
 * And a fail-closed default would make the door depend on a migration landing in
 * production. `user_metric_prefs` (Task 10) has been written, committed, and is
 * STILL not applied — so "the row will be there" is not an assumption this phase
 * has earned.
 *
 * The kill switch therefore works by ADDING the row with `enabled = false`
 * (migration 20260902100000 seeds it as `true` so an admin has something to
 * flip). Only an explicit `false` hides the door.
 */

export interface HistoryDoorQueryState {
  /**
   * react-query `data`: the `enabled` column of the `metrics_history_door` row.
   * `null` = the row does not exist. `undefined` = the read has not settled, or
   * it errored.
   */
  enabled: boolean | null | undefined;
}

/** Visible unless config says, in so many words, that it should not be. */
export function isHistoryDoorVisible({ enabled }: HistoryDoorQueryState): boolean {
  return enabled !== false;
}
